import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronDown,
  faChevronRight,
  faCopy,
  faDownload,
  faEllipsisV,
  faExchangeAlt,
  faFile,
  faFileAlt,
  faFileArchive,
  faFileAudio,
  faFileCode,
  faFileImage,
  faFilePdf,
  faFileVideo,
  faFolder,
  faFolderOpen,
  faFolderPlus,
  faPen,
  faTrash,
} from '@fortawesome/free-solid-svg-icons';
import { Divider, ListSubheader, MenuItem, Popover } from '@material-ui/core';
import PopupState, { bindPopover, bindTrigger } from 'material-ui-popup-state';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { IFileNode } from '../../types';

function sortNodes(nodes: IFileNode[]): IFileNode[] {
  return [...nodes].sort((a, b) => {
    if (a.type !== b.type) return a.type === 'directory' ? -1 : 1;
    return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
  });
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const diff = now.getTime() - d.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function getFileIcon(name: string) {
  const ext = (name.split('.').pop() ?? '').toLowerCase();
  if (['png', 'jpg', 'jpeg', 'gif', 'svg', 'webp', 'bmp', 'ico'].includes(ext)) return faFileImage;
  if (['mp3', 'wav', 'ogg', 'flac', 'm4a', 'aac'].includes(ext)) return faFileAudio;
  if (['mp4', 'avi', 'mkv', 'mov', 'webm'].includes(ext)) return faFileVideo;
  if (ext === 'pdf') return faFilePdf;
  if (['js', 'ts', 'tsx', 'jsx', 'php', 'py', 'java', 'c', 'cpp', 'cs', 'go', 'rs', 'rb', 'html', 'css', 'json', 'xml', 'yaml', 'yml', 'sh'].includes(ext)) return faFileCode;
  if (['zip', 'tar', 'gz', 'bz2', 'rar', '7z'].includes(ext)) return faFileArchive;
  if (['txt', 'md', 'csv', 'log'].includes(ext)) return faFileAlt;
  return faFile;
}

const DRAG_TYPE = 'application/x-file-tree-path';

interface IProps {
  node: IFileNode;
  depth?: number;
  onFileClick: (path: string, name: string) => void;
  onRename: (oldPath: string, newName: string) => Promise<void>;
  onDelete: (path: string, isDir: boolean) => Promise<void>;
  onCreateFolder: (parentPath: string, name: string) => Promise<void>;
  onCreateFile: (parentPath: string, name: string) => Promise<void>;
  onDownload?: (path: string, name: string) => void;
  onMove?: (sourcePath: string, destinationDir: string) => Promise<void>;
  onMoveToProject?: (sourcePath: string, targetProjectId: number) => Promise<void>;
  otherProjects?: Array<{ id: number; name: string }>;
}

const FileTreeNode: React.FC<IProps> = ({
  node,
  depth = 0,
  onFileClick,
  onRename,
  onDelete,
  onCreateFolder,
  onCreateFile,
  onDownload,
  onMove,
  onMoveToProject,
  otherProjects,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [renameName, setRenameName] = useState('');
  const [creatingChild, setCreatingChild] = useState<'file' | 'folder' | null>(null);
  const [newChildName, setNewChildName] = useState('');
  const [dropOver, setDropOver] = useState(false);
  const renameInputRef = useRef<HTMLInputElement>(null);
  const createInputRef = useRef<HTMLInputElement>(null);
  const isDir = node.type === 'directory';

  useEffect(() => {
    if (renaming && renameInputRef.current) {
      renameInputRef.current.focus();
      const dotIndex = renameName.lastIndexOf('.');
      renameInputRef.current.setSelectionRange(0, dotIndex > 0 ? dotIndex : renameName.length);
    }
  }, [renaming, renameName]);

  useEffect(() => {
    if (creatingChild && createInputRef.current) {
      createInputRef.current.focus();
    }
  }, [creatingChild]);

  const handleClick = () => {
    if (renaming) return;
    if (isDir) {
      setExpanded(!expanded);
    } else {
      onFileClick(node.path, node.name);
    }
  };

  const handleStartRename = () => {
    setRenameName(node.name);
    setRenaming(true);
  };

  const handleConfirmRename = async () => {
    const trimmed = renameName.trim();
    if (trimmed && trimmed !== node.name) {
      await onRename(node.path, trimmed);
    }
    setRenaming(false);
  };

  const handleRenameKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleConfirmRename();
    } else if (e.key === 'Escape') {
      setRenaming(false);
    }
  };

  const handleStartCreate = (type: 'file' | 'folder') => {
    setCreatingChild(type);
    setNewChildName('');
    setExpanded(true);
  };

  const handleConfirmCreate = async () => {
    const trimmed = newChildName.trim();
    if (trimmed && creatingChild) {
      if (creatingChild === 'folder') {
        await onCreateFolder(node.path, trimmed);
      } else {
        await onCreateFile(node.path, trimmed);
      }
    }
    setCreatingChild(null);
    setNewChildName('');
  };

  const handleCreateKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleConfirmCreate();
    } else if (e.key === 'Escape') {
      setCreatingChild(null);
      setNewChildName('');
    }
  };

  const handleDelete = () => {
    onDelete(node.path, isDir);
  };

  const handleCopyPath = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(node.path);
  };

  const handleDragStart = (e: React.DragEvent) => {
    e.dataTransfer.setData(DRAG_TYPE, node.path);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    if (!isDir || !e.dataTransfer.types.includes(DRAG_TYPE)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDropOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    if (e.currentTarget.contains(e.relatedTarget as Node)) return;
    setDropOver(false);
  };

  const handleTreeDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDropOver(false);
    const sourcePath = e.dataTransfer.getData(DRAG_TYPE);
    if (!sourcePath || !onMove || sourcePath === node.path || node.path.startsWith(sourcePath + '/')) return;
    await onMove(sourcePath, node.path);
  };

  const childProps = { onFileClick, onRename, onDelete, onCreateFolder, onCreateFile, onDownload, onMove, onMoveToProject, otherProjects };
  const [moveMenuAnchor, setMoveMenuAnchor] = useState<HTMLElement | null>(null);

  return (
    <>
      <Row
        style={{ paddingLeft: depth * 16 + 4 }}
        draggable={!renaming}
        onDragStart={handleDragStart}
        onDragOver={isDir ? handleDragOver : undefined}
        onDragLeave={isDir ? handleDragLeave : undefined}
        onDrop={isDir ? handleTreeDrop : undefined}
        $dropOver={dropOver}
      >
        <RowContent onClick={handleClick}>
          {isDir && <ChevronIcon icon={expanded ? faChevronDown : faChevronRight} />}
          <NodeIcon icon={isDir ? (expanded ? faFolderOpen : faFolder) : getFileIcon(node.name)} $isDir={isDir} />
          {renaming ? (
            <InlineInput
              ref={renameInputRef}
              value={renameName}
              onChange={(e) => setRenameName(e.target.value)}
              onKeyDown={handleRenameKey}
              onBlur={handleConfirmRename}
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <NodeName title={node.name}>{node.name}</NodeName>
          )}
        </RowContent>
        <SizeCol>{!isDir && node.size != null ? formatSize(node.size) : ''}</SizeCol>
        <MetaActions>
          <DateCol>{node.modifiedAt ? formatDate(node.modifiedAt) : ''}</DateCol>
          {!renaming && (
          <ActionsOverlay>
            <HoverAction onClick={handleCopyPath} title="Copy path">
              <FontAwesomeIcon icon={faCopy} />
            </HoverAction>
            <PopupState variant="popover" popupId={`file-menu-${node.path}`}>
              {(popupState) => (
                <>
                  <HoverAction
                    {...bindTrigger(popupState)}
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation();
                      bindTrigger(popupState).onClick(e as any);
                    }}
                  >
                    <FontAwesomeIcon icon={faEllipsisV} />
                  </HoverAction>
                  <Popover
                    {...bindPopover(popupState)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                  >
                    {isDir && (
                      <MenuItem
                        onClick={() => {
                          handleStartCreate('folder');
                          popupState.close();
                        }}
                      >
                        <MenuIcon icon={faFolderPlus} /> New Folder
                      </MenuItem>
                    )}
                    {!isDir && onDownload && (
                      <MenuItem
                        onClick={() => {
                          onDownload(node.path, node.name);
                          popupState.close();
                        }}
                      >
                        <MenuIcon icon={faDownload} /> Download
                      </MenuItem>
                    )}
                    <MenuItem
                      onClick={() => {
                        handleStartRename();
                        popupState.close();
                      }}
                    >
                      <MenuIcon icon={faPen} /> Rename
                    </MenuItem>
                    {onMoveToProject && otherProjects && otherProjects.length > 0 && (
                      <MenuItem
                        onClick={(e) => setMoveMenuAnchor(e.currentTarget)}
                      >
                        <MenuIcon icon={faExchangeAlt} /> Move to project…
                      </MenuItem>
                    )}
                    <MenuItem
                      onClick={() => {
                        handleDelete();
                        popupState.close();
                      }}
                    >
                      <DeleteIcon icon={faTrash} /> <DeleteText>Delete</DeleteText>
                    </MenuItem>
                  </Popover>
                  {onMoveToProject && otherProjects && (
                    <Popover
                      open={Boolean(moveMenuAnchor)}
                      anchorEl={moveMenuAnchor}
                      onClose={() => setMoveMenuAnchor(null)}
                      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                    >
                      <ListSubheader disableSticky>Move to project</ListSubheader>
                      <Divider />
                      {otherProjects.map((p) => (
                        <MenuItem
                          key={p.id}
                          onClick={async () => {
                            setMoveMenuAnchor(null);
                            popupState.close();
                            await onMoveToProject(node.path, p.id);
                          }}
                        >
                          <MenuIcon icon={faFolder} /> {p.name}
                        </MenuItem>
                      ))}
                    </Popover>
                  )}
                </>
              )}
            </PopupState>
          </ActionsOverlay>
          )}
        </MetaActions>
      </Row>
      {isDir && expanded && (
        <>
          {creatingChild && (
            <Row style={{ paddingLeft: (depth + 1) * 16 + 4 }}>
              <RowContent>
                <NodeIcon
                  icon={creatingChild === 'folder' ? faFolder : faFile}
                  $isDir={creatingChild === 'folder'}
                />
                <InlineInput
                  ref={createInputRef}
                  value={newChildName}
                  onChange={(e) => setNewChildName(e.target.value)}
                  onKeyDown={handleCreateKey}
                  onBlur={handleConfirmCreate}
                  placeholder={creatingChild === 'folder' ? 'folder name' : 'file name'}
                />
              </RowContent>
            </Row>
          )}
          {node.children && sortNodes(node.children).map((child) => (
            <FileTreeNode key={child.path} node={child} depth={depth + 1} {...childProps} />
          ))}
        </>
      )}
    </>
  );
};

export default FileTreeNode;

const Row = styled.div<{ $dropOver?: boolean }>`
  display: flex;
  align-items: center;
  padding: 2px 4px;
  cursor: pointer;
  border-radius: 3px;
  background: ${(p) => (p.$dropOver ? '#e3f2fd' : 'transparent')};
  border: 1px solid ${(p) => (p.$dropOver ? '#1976d2' : 'transparent')};
  &:hover {
    background: ${(p) => (p.$dropOver ? '#e3f2fd' : '#eee')};
  }
`;

const RowContent = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
`;

const ChevronIcon = styled(FontAwesomeIcon)`
  font-size: 9px;
  width: 10px;
  margin-right: 2px;
  color: #999;
`;

const NodeIcon = styled(FontAwesomeIcon)<{ $isDir: boolean }>`
  font-size: 12px;
  margin-right: 6px;
  color: ${(p) => (p.$isDir ? '#f9a825' : '#90a4ae')};
`;

const NodeName = styled.span`
  font-size: 13px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const MetaCol = styled.span`
  font-size: 10px;
  color: #bbb;
  white-space: nowrap;
  text-align: right;
  flex-shrink: 0;
`;

const SizeCol = styled(MetaCol)`
  width: 48px;
  ${Row}:hover & {
    display: none;
  }
`;

const DateCol = styled(MetaCol)`
  position: absolute;
  right: 0;
  ${Row}:hover & {
    opacity: 0;
  }
`;

const MetaActions = styled.div`
  position: relative;
  width: 52px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;

const ActionsOverlay = styled.div`
  position: absolute;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;

const HoverAction = styled.div`
  opacity: 0;
  padding: 0 4px;
  cursor: pointer;
  color: #999;
  font-size: 11px;
  ${Row}:hover & {
    opacity: 1;
  }
  &:hover {
    color: #555;
  }
`;

const InlineInput = styled.input`
  font-size: 13px;
  border: 1px solid #1976d2;
  border-radius: 2px;
  padding: 0 4px;
  outline: none;
  flex: 1;
  min-width: 0;
`;

const MenuIcon = styled(FontAwesomeIcon)`
  font-size: 12px;
  margin-right: 8px;
  color: #666;
`;

const DeleteIcon = styled(FontAwesomeIcon)`
  font-size: 12px;
  margin-right: 8px;
  color: #d32f2f;
`;

const DeleteText = styled.span`
  color: #d32f2f;
`;
